/*eslint-disable*/
import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Input, message, Modal, Row, Tooltip } from 'antd';
import { NumericFormat } from 'react-number-format';
import { withValueCap } from 'pages/globals/global';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GrossRentExcelModal from '../LeaseUtils/Excel/GrossRentExcelModal';
import GrossRentHistory from '../LeaseUtils/History/GrossRentHistory';

/**
 * Enhanced GrossRent component that provides a dynamic table for users to add
 * and edit monthly gross rent values directly in the CardAccordion.
 *
 * Now with Excel import functionality for bulk data entry.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.form - Form instance from parent
 * @param {string} props.screen - Current screen mode ('CREATE', 'EDIT', or 'VIEW')
 * @param {string} props.formListName - Name of form list for the table data
 */
export const transformRangeDataToAmountArray = (rangeData) => {
	// Initialize an empty result array
	const result = [];

	// Process each range object
	rangeData.forEach((range) => {
		const fromValue = parseInt(range.from);
		const toValue = parseInt(range.to);

		// Skip invalid ranges
		if (isNaN(fromValue) || isNaN(toValue) || fromValue > toValue) {
			console.warn('Invalid range found:', range);
			return;
		}

		// Convert amount string to number (remove commas)
		const amount = parseFloat(String(range.amount).replace(/,/g, ''));

		// Skip if amount is not a valid number
		if (isNaN(amount)) {
			console.warn('Invalid amount found:', range);
			return;
		}

		// Calculate how many times to repeat the amount ((to - from) + 1)
		const repetitions = toValue - fromValue + 1;

		// Add the amount to the result array the specified number of times
		for (let i = 0; i < repetitions; i++) {
			result.push(amount);
		}
	});

	return result;
};
const GrossRent = ({ form, screen = 'CREATE', formListName = 'grossRentList', grossRentListSet = [] }) => {
	const [openModal, setOpenModal] = useState(false);
	const grossRentListSetForHistory = [...grossRentListSet]?.slice(0, -1);

	// State to track which values have been modified (for highlighting)
	const [modifiedFields, setModifiedFields] = useState({});
	// State to control the Excel modal visibility
	const [excelModalVisible, setExcelModalVisible] = useState(false);

	// Calculate the total from all amount fields immediately when values change
	const calculateTotal = () => {
		if (!form || !formListName) return;

		try {
			// Get the current values from the form
			const values = form.getFieldValue(formListName);

			if (values && Array.isArray(values)) {
				// Also save this to the main form for API submission
				form.setFieldValue(
					'grossRents',
					values.map((item) => (item.amount ? parseFloat(String(item.amount).replace(/,/g, '')) : 0))
				);
			}
		} catch (error) {
			console.error('Error calculating total:', error);
		}
	};

	// Handler for when an amount changes
	const handleAmountChange = (fieldName) => {
		// Mark this field as modified for highlighting
		setModifiedFields((prev) => ({
			...prev,
			[fieldName]: true,
		}));

		// Recalculate the total
		setTimeout(calculateTotal, 10);
	};

	// Handler for "To" value changes
	const handleToChange = (fieldName) => {
		// Only mark this field as modified for highlighting
		setModifiedFields((prev) => ({
			...prev,
			[fieldName]: true,
		}));
	};

	// Validate To value ONLY on blur to allow typing multi-digit numbers
	const validateToValueOnBlur = (index) => {
		// Get the current values
		const currentValues = form.getFieldValue(formListName);
		if (!currentValues) return;

		const toValue = parseInt(currentValues[index].to);
		const fromValue = parseInt(currentValues[index].from);

		// Validate that To is greater than or equal to From
		if (!isNaN(toValue) && !isNaN(fromValue) && toValue < fromValue) {
			message.error("'To' value must be greater than or equal to 'From' value");
			form.setFields([
				{
					name: [formListName, index, 'to'],
					value: '',
					errors: ["'To' value must be greater than 'From' value"],
				},
			]);
		}
	};

	// Validate if a row is complete and valid
	const isRowValid = (row) => {
		if (!row) return false;

		// Check if all required fields are filled
		const toValue = parseInt(row.to);
		const fromValue = parseInt(row.from);

		if (isNaN(toValue) || toValue <= 0) return false;
		if (isNaN(fromValue) || fromValue <= 0) return false;
		if (toValue < fromValue) return false;
		if (!row.amount) return false;

		return true;
	};

	// Handle adding a new row
	const handleAddRow = () => {
		const currentValues = form.getFieldValue(formListName);

		// Initialize with first row if no values
		if (!currentValues || !currentValues.length) {
			form.setFieldValue(formListName, [{ from: 1, to: '', amount: '', remarks: '' }]);
			return;
		}

		// Get the last row to validate
		const lastRow = currentValues[currentValues.length - 1];

		// Check if the last row is complete
		if (!isRowValid(lastRow)) {
			message.error('Please complete all required fields in the current row before adding a new one');
			return;
		}

		// Get the "to" value from the last row
		const lastToValue = parseInt(lastRow.to);

		// New row's "From" value is last row's "To" value + 1
		const newFrom = lastToValue + 1;

		// Add the new row
		const newRow = { from: newFrom, to: '', amount: '', remarks: '' };
		form.setFieldValue(formListName, [...currentValues, newRow]);
	};

	// Handle the Excel data being applied from the Excel modal
	const handleExcelDataApplied = (data) => {
		// console.log('Applying Excel data to form:', data);

		// Update the form with the Excel data
		form.setFieldValue(formListName, data);

		// Recalculate total
		setTimeout(calculateTotal, 10);

		// Mark all fields as modified
		const newModifiedFields = {};
		data.forEach((_, index) => {
			newModifiedFields[`${formListName}.${index}.to`] = true;
			newModifiedFields[`${formListName}.${index}.amount`] = true;
			newModifiedFields[`${formListName}.${index}.remarks`] = true;
		});
		setModifiedFields(newModifiedFields);

		// Show success message
		message.success(`Successfully imported ${data.length} rows of data`);
	};

	const handleApply = () => {
		const currentValues = form.getFieldValue(formListName);
		if (!currentValues || currentValues.length === 0) return;
		const transformedData = transformRangeDataToAmountArray(currentValues);
		form.setFieldValue('grossRents', transformedData);
		message.success('Data applied successfully');
	};

	// Initialize with the first row
	useEffect(() => {
		const currentValues = form.getFieldValue(formListName);
		if (!currentValues || !currentValues.length) {
			// Initialize with one row, from=1
			form.setFieldValue(formListName, [{ from: 1, to: '', amount: '', remarks: '' }]);
		}

		// Calculate initial total if data exists
		calculateTotal();
	}, [form, formListName]);

	const handleHistory = () => {
		setOpenModal(true);
	};

	return (
		<>
			<Form.List name={formListName}>
				{(fields) => (
					<>
						<Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
							<Col>
								<Button type="primary" onClick={handleAddRow} disabled={screen === 'VIEW'}>
									Add Row
								</Button>
							</Col>
							<Col>
								<Tooltip title="Import from Excel">
									<Button
										type="default"
										onClick={() => setExcelModalVisible(true)}
										disabled={screen === 'VIEW'}
										icon={<CloudUploadIcon style={{ fontSize: '16px' }} />}
										style={{ marginLeft: '8px' }}
									>
										Excel Import
									</Button>
								</Tooltip>
							</Col>
							<Col>
								<Button type="primary" onClick={handleApply} disabled={screen === 'VIEW'}>
									Apply
								</Button>
							</Col>
							{grossRentListSetForHistory?.length !== 0 && (screen?.toLowerCase() === 'view' || screen?.toLowerCase() === 'edit') && (
								<Col>
									<Button type="primary" onClick={handleHistory}>
										History
									</Button>
								</Col>
							)}
						</Row>

						<TableContainer
							className="gross-rent-table"
							sx={{
								'& table, & th, & td': {
									border: '1px solid #e0e0e0',
									borderCollapse: 'collapse',
								},
								'& th, & td': {
									p: '5px',
								},
								'& td .ant-form-item': {
									marginBottom: '0px',
								},

								'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '20%' } /* From/From Month */,
								'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '20%' } /* To/To Month */,
								'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '20%' } /* Amount/Monthly Adjustment */,
								'& th:nth-of-type(4), & td:nth-of-type(4)': { width: '20%' } /* Remarks/Total Amount */,
								'& th:nth-of-type(5), & td:nth-of-type(5)': { width: '20%' } /* Action */,
								'& table': { width: '100%' } /* Ensure table takes full container width */,
							}}
						>
							<Table aria-label="Gross Rent table">
								<TableHead>
									<TableRow>
										<TableCell sx={{ textAlign: 'center' }}>From Month</TableCell>
										<TableCell sx={{ textAlign: 'center' }}>To Month</TableCell>
										<TableCell sx={{ textAlign: 'center' }}>Amount</TableCell>
										<TableCell sx={{ textAlign: 'center' }}>Notes</TableCell>
										<TableCell sx={{ textAlign: 'center' }}>Action</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{fields.length > 0 ? (
										fields.map((field, index) => (
											<TableRow key={field.key}>
												<TableCell>
													<Form.Item name={[field.name, 'from']}>
														<Input
															disabled={true} // Always disabled to prevent manual editing
															style={{
																padding: '8px',
																width: '100%',
																backgroundColor: '#f5f5f5', // Grey background to indicate disabled
																textAlign: 'center',
															}}
														/>
													</Form.Item>
												</TableCell>
												<TableCell>
													<Form.Item
														name={[field.name, 'to']}
														rules={[
															{ required: false, message: "Please enter a 'To' value" },
															({ getFieldValue }) => ({
																validator(_, value) {
																	const fromValue = getFieldValue([formListName, field.name, 'from']);
																	if (!value || !fromValue) return Promise.resolve();
																	if (parseInt(value) <= parseInt(fromValue)) {
																		return Promise.reject(new Error("'To' must be greater than 'From'"));
																	}
																	return Promise.resolve();
																},
															}),
														]}
													>
														<Input
															type="number"
															disabled={screen === 'VIEW'}
															placeholder="Enter To value"
															style={{
																width: '100%',
																padding: '8px',

																borderColor: modifiedFields[`${formListName}.${field.name}.to`]
																	? '#1890ff'
																	: undefined,
																textAlign: 'center',
															}}
															onChange={() => handleToChange(`${formListName}.${field.name}.to`)}
															onBlur={() => validateToValueOnBlur(index)}
														/>
													</Form.Item>
												</TableCell>
												<TableCell>
													<Form.Item
														name={[field.name, 'amount']}
														rules={[{ required: false, message: 'Please enter an amount' }]}
													>
														<NumericFormat
															size="small"
															decimalScale={2}
															placeholder="0.00"
															fixedDecimalScale
															thousandSeparator=","
															isAllowed={withValueCap}
															style={{
																padding: '18px',
																textAlign: 'right',
																height: '32px',
																borderRadius: '4px',
																fontSize: '13px',
																borderColor: modifiedFields[`${formListName}.${field.name}.amount`]
																	? '#1890ff'
																	: undefined,
																backgroundColor: modifiedFields[`${formListName}.${field.name}.amount`]
																	? '#f0f7ff'
																	: undefined,
															}}
															customInput={Input}
															disabled={screen === 'VIEW'}
															onValueChange={(values) => {
																// console.log('🚀 ~ GrossRent ~ values:', values);
																handleAmountChange(`${formListName}.${field.name}.amount`);
																// Get the current form values
																const currentValues = [...form.getFieldValue(formListName)];

																// Update the current field with the new value (could be empty)
																if (currentValues[index]) {
																	// Update with the numeric value (not formatted), or empty string if deleted
																	currentValues[index].amount = values.value || '';

																	// Update the form with the new value
																	form.setFieldValue(formListName, currentValues);
																}
															}}
															// Add onBlur handler to ensure value is recalculated even if NumericFormat doesn't trigger
															onBlur={() => setTimeout(calculateTotal, 10)}
														/>
													</Form.Item>
												</TableCell>
												<TableCell>
													<Form.Item name={[field.name, 'remarks']}>
														<Input
															placeholder="Enter remarks"
															disabled={screen === 'VIEW'}
															style={{
																padding: '8px',
																borderColor: modifiedFields[`${formListName}.${field.name}.remarks`]
																	? '#1890ff'
																	: undefined,
															}}
															onChange={() =>
																setModifiedFields((prev) => ({
																	...prev,
																	[`${formListName}.${field.name}.remarks`]: true,
																}))
															}
														/>
													</Form.Item>
												</TableCell>
												<TableCell align="center">
													{screen !== 'VIEW' && index > 0 && (
														<Button
															danger
															type="primary"
															onClick={() => {
																const currentValues = [...form.getFieldValue(formListName)];

																// Remove the row
																currentValues.splice(index, 1);

																// Fix the From/To sequence if needed
																for (let i = index; i < currentValues.length; i++) {
																	// If not the first row, From should be previous row's To + 1
																	if (i > 0) {
																		const prevTo = parseInt(currentValues[i - 1].to);
																		if (!isNaN(prevTo)) {
																			currentValues[i].from = prevTo + 1;
																		}
																	}
																}

																form.setFieldValue(formListName, currentValues);
															}}
														>
															Delete
														</Button>
													)}
												</TableCell>
											</TableRow>
										))
									) : (
										<TableRow>
											<TableCell colSpan={5} style={{ textAlign: 'center' }}>
												No data. Click "Add Row" to add new entries.
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>

						{/* Hidden form field to store processed data for API submission */}
						<Form.Item name="grossRents" noStyle />
					</>
				)}
			</Form.List>

			{/* Excel import modal */}
			<GrossRentExcelModal
				visible={excelModalVisible}
				onCancel={() => setExcelModalVisible(false)}
				onApply={handleExcelDataApplied}
				existingData={form.getFieldValue(formListName) || []}
			/>
			<Modal
				title={'Gross Rent Modifications'}
				open={openModal}
				onCancel={() => setOpenModal(false)}
				width={800}
				footer={[
					<Button key="cancel" onClick={() => setOpenModal(false)}>
						Close
					</Button>,
				]}
			>
				{grossRentListSetForHistory?.map((item, index) => (
					<GrossRentHistory key={index} data={item} index={index} />
				))}
			</Modal>
		</>
	);
};

export default GrossRent;
